import Phaser from './lib/phaser.js';
import { SCENE_KEYS } from './scenes/scene-keys.js';
import { netManager } from './net/index.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from './utils/data-manager.js';
import { PreloadScene } from './scenes/preload-scene.js';
import { BattleScene } from './scenes/battle-scene.js';
import { WorldScene } from './scenes/world-scene.js';
import { EntryScene } from './scenes/entry-scene.js';
import { ProfileSetupScene } from './scenes/profile-setup-scene.js';
import { ProfileScene } from './scenes/profile-scene.js';
import { OptionsScene } from './scenes/options-scene.js';
import { TestScene } from './scenes/test-scene.js';
import { MonsterPartyScene } from './scenes/monster-party-scene.js';
import { PcBoxScene } from './scenes/pc-box-scene.js';
import { MonsterDetailsScene } from './scenes/monster-details-scene.js';
import { InventoryScene } from './scenes/inventory-scene.js';
import { CutsceneScene } from './scenes/cutscene-scene.js';
import { DialogScene } from './scenes/dialog-scene.js';
import { CurrencyHudScene } from './scenes/currency-hud-scene.js';
import { QuestsScene } from './scenes/quests-scene.js';
import { MarketplaceScene } from './scenes/marketplace-scene.js';
import { PvpBattleScene } from './scenes/pvp-battle-scene.js';
import { CardShopScene } from './scenes/card-shop-scene.js';
import { WorldHudScene } from './scenes/world-hud-scene.js';
import { SettingsScene } from './scenes/settings-scene.js';
import { MapScene } from './scenes/map-scene.js';
import { LeaderboardScene } from './scenes/leaderboard-scene.js';
import { FriendsScene } from './scenes/friends-scene.js';
import { MobileControlsScene } from './scenes/mobile-controls-scene.js';
import { EvolutionScene } from './scenes/evolution-scene.js';
import { TapFeedbackScene } from './scenes/tap-feedback-scene.js';
import { HowToPlayScene } from './scenes/how-to-play-scene.js';
import { VaultScene } from './scenes/vault-scene.js';

// ── On-screen error overlay (diagnostic) ──────────────────────────────────────
// Surfaces uncaught errors + unhandled promise rejections as a visible red banner so a black/
// frozen screen always shows WHY (a screenshot is enough to diagnose), instead of failing silently.
(function installErrorOverlay() {
  function show(label, err) {
    try {
      const msg = err && err.stack ? err.stack : err && err.message ? err.message : String(err);
      let el = document.getElementById('mn-error-overlay');
      if (!el) {
        el = document.createElement('div');
        el.id = 'mn-error-overlay';
        el.style.cssText =
          'position:fixed;left:0;right:0;bottom:0;max-height:42vh;overflow:auto;z-index:99999;' +
          'background:rgba(40,0,0,0.94);color:#ff8a8a;font:12px/1.4 monospace;padding:10px 12px;' +
          'white-space:pre-wrap;border-top:2px solid #ff4d4d;';
        el.addEventListener('click', () => el.remove());
        document.body.appendChild(el);
      }
      el.textContent = `⚠️ ${label}: ${msg}\n\n(tap to dismiss)`;
    } catch {
      /* never let the error reporter itself throw */
    }
  }
  window.addEventListener('error', (e) => show('Error', e.error || e.message));
  window.addEventListener('unhandledrejection', (e) => show('Unhandled rejection', e.reason));
})();

const game = new Phaser.Game({
  type: Phaser.CANVAS,
  // Pixel-art rendering: NEAREST filtering + roundPixels so the 32px character/creature/tile
  // sprites stay CRISP when upscaled (linear filtering made avatars blurry). Correct for a
  // pixel-art monster game.
  pixelArt: true,
  // Perf: snap every draw to whole pixels. At camera zoom 0.5 the huge world background
  // (2560x5184) and the player land on fractional sub-pixel coordinates every frame, which
  // forces the canvas renderer to resample large textures continuously and causes shimmer +
  // dropped frames. roundPixels eliminates that per-frame resampling for smooth 60fps movement.
  roundPixels: true,
  // Cap the simulation/render loop at 60fps (deltaHistory smooths spikes). Without a target,
  // Phaser will happily render as fast as the display allows, wasting work on the big map.
  fps: { target: 60, forceSetTimeOut: false },
  render: {
    antialias: false, // pixel-art: keep edges crisp (pixelArt:true forces this anyway)
    transparent: false,
    powerPreference: 'high-performance',
  },
  scale: {
    parent: 'game-container',
    // Responsive: the canvas tracks the browser window so there are no grey
    // letterbox bars, giving the world scene a wide MMO-style viewport. UI scenes
    // read `this.scale.width/height` so they re-center at any viewport size.
    // width/height act as the initial size before the first resize.
    width: window.innerWidth,
    height: window.innerHeight,
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    // Round the canvas/display size so a RESIZE never lands on a fractional pixel size,
    // which otherwise re-triggers layout + texture rescales on the world background.
    autoRound: true,
  },
  backgroundColor: '#000000',
});

game.scene.add(SCENE_KEYS.PRELOAD_SCENE, PreloadScene);
game.scene.add(SCENE_KEYS.WORLD_SCENE, WorldScene);
game.scene.add(SCENE_KEYS.BATTLE_SCENE, BattleScene);
game.scene.add(SCENE_KEYS.ENTRY_SCENE, EntryScene);
game.scene.add(SCENE_KEYS.PROFILE_SETUP_SCENE, ProfileSetupScene);
game.scene.add(SCENE_KEYS.PROFILE_SCENE, ProfileScene);
game.scene.add(SCENE_KEYS.OPTIONS_SCENE, OptionsScene);
game.scene.add(SCENE_KEYS.TEST_SCENE, TestScene);
game.scene.add(SCENE_KEYS.MONSTER_PARTY_SCENE, MonsterPartyScene);
game.scene.add(SCENE_KEYS.PC_BOX_SCENE, PcBoxScene);
game.scene.add(SCENE_KEYS.MONSTER_DETAILS_SCENE, MonsterDetailsScene);
game.scene.add(SCENE_KEYS.INVENTORY_SCENE, InventoryScene);
game.scene.add(SCENE_KEYS.CUTSCENE_SCENE, CutsceneScene);
game.scene.add(SCENE_KEYS.DIALOG_SCENE, DialogScene);
game.scene.add(SCENE_KEYS.CURRENCY_HUD_SCENE, CurrencyHudScene);
game.scene.add(SCENE_KEYS.QUESTS_SCENE, QuestsScene);
game.scene.add(SCENE_KEYS.MARKETPLACE_SCENE, MarketplaceScene);
game.scene.add(SCENE_KEYS.PVP_BATTLE_SCENE, PvpBattleScene);
game.scene.add(SCENE_KEYS.CARD_SHOP_SCENE, CardShopScene);
game.scene.add(SCENE_KEYS.WORLD_HUD_SCENE, WorldHudScene);
game.scene.add(SCENE_KEYS.SETTINGS_SCENE, SettingsScene);
game.scene.add(SCENE_KEYS.MAP_SCENE, MapScene);
game.scene.add(SCENE_KEYS.LEADERBOARD_SCENE, LeaderboardScene);
game.scene.add(SCENE_KEYS.FRIENDS_SCENE, FriendsScene);
game.scene.add(SCENE_KEYS.MOBILE_CONTROLS_SCENE, MobileControlsScene);
game.scene.add(SCENE_KEYS.EVOLUTION_SCENE, EvolutionScene);
game.scene.add(SCENE_KEYS.TAP_FEEDBACK_SCENE, TapFeedbackScene);
game.scene.add(SCENE_KEYS.HOW_TO_PLAY_SCENE, HowToPlayScene);
game.scene.add(SCENE_KEYS.VAULT_SCENE, VaultScene);
game.scene.start(SCENE_KEYS.PRELOAD_SCENE);
// Always-on, top-most tap-feedback ripple overlay. Launched (parallel) so it persists across every
// scene transition and renders above all of them; purely visual, never blocks input.
game.scene.start(SCENE_KEYS.TAP_FEEDBACK_SCENE);

// ── Onboarding / username input fix ──────────────────────────────────────────
// Phaser's global keyboard manager preventDefaults the keys it captures (movement + hotkeys are
// registered via addKey, which auto-captures), so typing those letters in an HTML <input> (e.g. the
// username field) silently did nothing — "some letters work and some don't". While any text field is
// focused, disable Phaser keyboard processing so EVERY key reaches the DOM input; restore on blur.
const setGameKeyboard = (on) => {
  if (game.input?.keyboard) game.input.keyboard.enabled = on;
};
const isTextField = (el) =>
  el instanceof HTMLElement && el.matches('input, textarea, [contenteditable="true"]');
document.addEventListener('focusin', (e) => {
  if (isTextField(e.target)) setGameKeyboard(false);
});
document.addEventListener('focusout', (e) => {
  if (isTextField(e.target)) setGameKeyboard(true);
});
// Self-heal: any pointer-down that is NOT on a text field re-enables the game keyboard. Without this,
// if a focused input was removed from the DOM without firing focusout (e.g. closing the chat right as
// an encounter starts), the keyboard could stay disabled forever — leaving SPACE/arrows dead and the
// player "stuck" at the battle move menu. A single tap anywhere now restores it.
document.addEventListener(
  'pointerdown',
  (e) => {
    if (!isTextField(e.target)) setGameKeyboard(true);
  },
  true
);

// ── Save the party on tab close / hide (anti "lost my beasts on disconnect") ──
// The 20s autosave + per-battle save cover most cases, but a player who catches/levels a monster and
// immediately closes the tab could lose it before the next save. sendBeacon survives page unload, so
// flush the current party to the server one last time on hide/close. Skips cache-only sessions (could
// be stale) and empty parties (the server ignores empty anyway, so it can never wipe a real team).
const flushPartyOnExit = () => {
  try {
    const API_URL = import.meta.env.VITE_API_URL;
    const wallet = netManager?.wallet;
    if (!API_URL || !wallet || netManager.partyFromCacheOnly) return;
    const party = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY);
    if (!Array.isArray(party) || party.length === 0) return;
    const blob = new Blob([JSON.stringify({ wallet, party })], { type: 'application/json' });
    navigator.sendBeacon?.(`${API_URL}/player/party`, blob);
  } catch {
    /* best-effort — never block unload */
  }
};
window.addEventListener('pagehide', flushPartyOnExit);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushPartyOnExit();
});
